import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
	AUTH_REJECTED_CODE,
	AUTH_REJECTED_MESSAGE,
} from 'src/app/config/auth-recovery.config';
import { TokenType } from 'src/app/config/endpoint-token-catalog';
import { selectPreAplicationState } from 'src/app/state/selectors/pre-application.selectors';
import { MonitoringService } from '../monitoring/monitoring.service';
import { OnboardingProcessFlowService } from '../onboarding-process-flow.service';
import {
	TurnstileAuthService,
	TurnstileOutcome,
} from '../turnstile-auth/turnstile-auth.service';
import { AuthTokenStoreService } from './auth-token-store.service';
import { OtpReauthenticationService } from './otp-reauthentication.service';
export type RefreshableTokenType = TokenType.INITIAL | TokenType.EVALUATION;

/**
 * Desenlaces de una reemisión de token. `REAUTH_REQUIRED` es el que evita el
 * rechazo: el socio está capturando un OTP nuevo, así que la petición se
 * descarta pero el proceso sigue vivo.
 */
export enum TokenRefreshOutcome {
	REFRESHED = 'REFRESHED',
	FAILED = 'FAILED',
	REAUTH_REQUIRED = 'REAUTH_REQUIRED',
	/**
	 * Cloudflare no pudo ejecutar el reto en el navegador del socio. Es el otro
	 * desenlace que evita el rechazo: nadie negó la credencial, así que el
	 * proceso sigue vivo y la petición simplemente falla.
	 */
	VERIFICATION_FAILED = 'VERIFICATION_FAILED',
}

@Injectable({ providedIn: 'root' })
export class AuthRecoveryService {

	private readonly inFlightRefresh = new Map<
		RefreshableTokenType,
		Observable<TokenRefreshOutcome>
	>();

	private hasRedirectedToRejected: boolean = false;

	constructor(
		private authTokenStore: AuthTokenStoreService,
		private turnstileAuthService: TurnstileAuthService,
		private otpReauthentication: OtpReauthenticationService,
		private monitoringService: MonitoringService,
		private onboardingProcessFlowService: OnboardingProcessFlowService,
		private store: Store
	) {}

	refreshToken(
		tokenType: RefreshableTokenType
	): Observable<TokenRefreshOutcome> {
		const inFlight = this.inFlightRefresh.get(tokenType);
		if (inFlight) {
			return inFlight;
		}

		const refreshed$ = new ReplaySubject<TokenRefreshOutcome>(1);
		this.inFlightRefresh.set(tokenType, refreshed$.asObservable());
		this.performRefresh(tokenType).subscribe({
			next: outcome => this.completeRefresh(tokenType, refreshed$, outcome),
			error: () =>
				this.completeRefresh(tokenType, refreshed$, TokenRefreshOutcome.FAILED),
		});
		return refreshed$.asObservable();
	}

	goToRejectedByPermissions(reason: string): void {
		if (this.hasRedirectedToRejected) {
			return;
		}
		this.hasRedirectedToRejected = true;
		this.monitoringService.captureError(
			new Error(`Permisos no validados: ${reason}`),
			{ location: 'AuthRecoveryService', declinedCode: AUTH_REJECTED_CODE }
		);
		this.authTokenStore.clearAll();
		this.otpReauthentication.cancel();
		this.onboardingProcessFlowService.changingStepCompleted();
		this.onboardingProcessFlowService.goToRejected(
			AUTH_REJECTED_MESSAGE,
			AUTH_REJECTED_CODE
		);
	}

	private completeRefresh(
		tokenType: RefreshableTokenType,
		refreshed$: ReplaySubject<TokenRefreshOutcome>,
		outcome: TokenRefreshOutcome
	): void {
		this.inFlightRefresh.delete(tokenType);
		refreshed$.next(outcome);
		refreshed$.complete();
	}

	private performRefresh(
		tokenType: RefreshableTokenType
	): Observable<TokenRefreshOutcome> {
		return tokenType === TokenType.EVALUATION
			? this.refreshEvaluationToken()
			: this.refreshInitialToken();
	}

	/**
	 * A media sesión el reto se reintenta a mano si los intentos automáticos se
	 * agotan: rendirse aquí le cuesta al socio todo el avance del proceso.
	 */
	private refreshInitialToken(): Observable<TokenRefreshOutcome> {
		return from(this.turnstileAuthService.initializeWithManualRetry()).pipe(
			map(outcome => {
				if (
					outcome === TurnstileOutcome.SUCCESS &&
					this.authTokenStore.isInitialTokenValid()
				) {
					return TokenRefreshOutcome.REFRESHED;
				}
				// Solo el emisor negando la credencial es un rechazo real. Un fallo
				// del widget es infraestructura del navegador y no debe terminar el
				// proceso.
				return outcome === TurnstileOutcome.ISSUER_FAILED
					? TokenRefreshOutcome.FAILED
					: TokenRefreshOutcome.VERIFICATION_FAILED;
			}),
			catchError(() => of(TokenRefreshOutcome.VERIFICATION_FAILED))
		);
	}

	/**
	 * El token de evaluación no se puede reemitir por detrás: `evaluation_steps_auths`
	 * exige un OTP y el que está en el store ya se consumió. Se manda al socio a
	 * la pantalla de verificación para que capture uno nuevo.
	 */
	private refreshEvaluationToken(): Observable<TokenRefreshOutcome> {
		return this.ensureInitialToken().pipe(
			switchMap(initialOutcome => {
				// Se propaga el desenlace del token inicial tal cual: si lo que falló
				// fue la verificación del navegador, este camino tampoco es un rechazo.
				if (initialOutcome !== TokenRefreshOutcome.REFRESHED) {
					return of(initialOutcome);
				}
				return this.store.select(selectPreAplicationState).pipe(
					take(1),
					map(preApplicationState =>
						preApplicationState?.applicantId &&
						this.otpReauthentication.start()
							? TokenRefreshOutcome.REAUTH_REQUIRED
							: TokenRefreshOutcome.FAILED
					)
				);
			})
		);
	}

	/**
	 * `evaluation_steps_auths` viaja con el token inicial en la cabecera, así que
	 * si ese también venció hay que reemitirlo antes de mandar al socio a
	 * capturar el OTP: sin él el emisor responde 403 y el proceso termina en
	 * rechazo con el código nuevo ya escrito.
	 */
	private ensureInitialToken(): Observable<TokenRefreshOutcome> {
		if (this.authTokenStore.isInitialTokenValid()) {
			return of(TokenRefreshOutcome.REFRESHED);
		}
		return this.refreshInitialToken();
	}
}
